/**
 * Java Packaging Executor
 *
 * Handles the 'generate-java-package' node execution.
 * Calls the ModernizeIT API Layer /java-packaging/start endpoint.
 *
 * API Flow:
 * 1. POST /java-packaging/start → returns job_id
 * 2. GET /java-packaging/status/{job_id} → check status
 * 3. GET /java-packaging/download/{job_id} → download ZIP
 * 4. GET /java-packaging/validation/{job_id} → get validation report
 *
 * This node packages Java code into a production-ready Spring Boot application:
 * - Creates Spring Boot project structure (pom.xml, Application.java)
 * - Organizes Java classes into proper packages
 * - Generates REST Controllers and Repositories
 * - Creates Dockerfile and docker-compose.yml
 * - Generates build scripts (build.sh, start.sh, stop.sh)
 * - Runs optional validation (compile check, dependency check)
 * - Produces downloadable ZIP package
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the java-package node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and package details
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing Java packaging...')

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Build the request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id, // REQUIRED 2026-01-14: For AWS credentials lookup
    source: config.source || 'refactor',
    // Source job IDs from workflow context (2026-01-18: Workflow run tracking)
    // Note: Key names must match what executors return (see each executor's formatResult)
    service_generation_job_id: context.jobs?.service_generation_job_id,
    code_refactor_job_id: context.jobs?.coderefactor_job_id,  // No underscore in key
    code_analysis_job_id: context.jobs?.codeanalysis_job_id,  // No underscore in key
    options: {
      spring_boot_version: config.spring_boot_version || '3.2.0',
      java_version: config.java_version || '17',
      include_docker: config.include_docker !== false,
      include_tests: config.include_tests !== false,
      run_validation: config.run_validation !== false,
    },
    // Target Profile ID — selected in node config panel
    target_profile_id: config.target_profile_id || null,
  }

  onProgress(5, 'Submitting Java packaging job...')

  // 2. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/java-packaging/start`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    // Check for API-level error in response
    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'Java packaging failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  onProgress(10, `Job started: ${jobId}`)

  // 3. Check if job completed immediately (synchronous execution)
  if (jobResponse.status === 'completed' || jobResponse.status === 'SUCCEEDED') {
    onProgress(50, 'Java packaging complete')
  } else {
    // 4. Worker mode: Poll for completion using shared authenticated poller
    onProgress(15, 'Packaging Java application...')
    try {
      const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
        maxWaitSeconds: 600,
        pollInterval: 2000,
        onProgress: (progress, message) => {
          // Map polling progress from 15-90%
          const mappedProgress = 15 + (progress * 0.75)
          onProgress(mappedProgress, message)
        },
        signal,
      })

      // Merge completed job data
      Object.assign(jobResponse, completedJob)
    } catch (pollError) {
      // Job failed — fetch output_data so artifact buttons still work for FAILED jobs.
      // CRITICAL: attaching nodeResult to the error lets useWorkflow.js store it in
      // executionProgress, which makes nodeHasOutputs() return true → Download Artifacts enabled.
      try {
        const statusResp = await fetch(
          `${apiBaseUrl}/java-packaging/status/${jobId}`,
          { headers: getAuthHeaders() }
        )
        if (statusResp.ok) {
          const failedData = await statusResp.json()
          const partialResult = formatResult(jobId, failedData, null)
          partialResult.failed = true
          const err = new Error(failedData.error || pollError.message || 'Java packaging failed')
          err.nodeResult = partialResult
          throw err
        }
      } catch (fetchErr) {
        if (fetchErr.nodeResult) throw fetchErr
      }
      throw pollError
    }
  }

  // 5. Get validation report (optional, fetched after job succeeds)
  onProgress(92, 'Retrieving validation report...')
  const validation = await getValidation(apiBaseUrl, jobId, signal)

  onProgress(100, 'Java packaging complete')

  // 6. Return formatted result with job data and validation
  return formatResult(jobId, jobResponse, validation)
}

/**
 * Get validation report for a job
 */
async function getValidation(apiBaseUrl, jobId, signal) {
  try {
    const response = await fetch(`${apiBaseUrl}/java-packaging/validation/${jobId}`, { headers: getAuthHeaders(), signal })
    if (response.ok) {
      return await response.json()
    }
  } catch (error) {
    // Validation is optional, don't fail if not available
    console.warn('Could not retrieve validation report:', error.message)
  }
  return null
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data, validation) {
  return {
    // Job tracking
    jobs: {
      java_packaging_job_id: jobId,
    },

    // Status
    success: data.success !== false,
    status: data.status || 'completed',

    // Paths
    output_path: data.output_path,
    package_path: data.package_path,
    download_url: data.download_url,

    // Execution metrics
    duration_ms: data.duration_ms,

    // Package info
    package_info: {
      name: data.package_name,
      version: data.package_version,
      spring_boot_version: data.spring_boot_version,
      java_version: data.java_version,
    },

    // Files generated
    files_generated: data.files_generated || [],
    file_count: data.file_count || 0,

    // Validation report
    validation: validation || data.validation || null,
    validation_passed: validation?.passed ?? data.validation_passed ?? null,
    validation_errors: validation?.errors ?? data.validation_errors ?? [],
    validation_warnings: validation?.warnings ?? data.validation_warnings ?? [],

    // Docker info
    docker_included: data.docker_included ?? false,
    dockerfile_path: data.dockerfile_path,

    // Source info
    source_type: data.source_type,
    source_path: data.source_path,

    // Generated timestamp
    generated_at: data.generated_at || null,

    // Error info if any
    error: data.error || null,
  }
}

export default {
  execute,
}
